// Read the accepted visit clock. Never turn it into opening hours, a booking,
// live routing, or a guarantee of the best light/crowds. Works offline.
const clockMinutes = value => {
  const match = /^(\d{2}):(\d{2})$/.exec(value || "");
  if (!match || +match[1] > 23 || +match[2] > 59) return null;
  return +match[1] * 60 + +match[2];
};

export const japanMoment = (now = new Date()) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(now).filter(p => p.type !== "literal").map(p => [p.type, p.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, minutes: +parts.hour * 60 + +parts.minute };
};

export const visitTiming = (loc, day) => {
  if (!loc || loc.is_optional || !/^\d{4}-\d{2}-\d{2}$/.test(day?.date || "")) return null;
  const iso = new Date(`${day.date}T00:00:00Z`);
  if (Number.isNaN(iso.getTime()) || iso.toISOString().slice(0, 10) !== day.date) return null;
  const plan = loc.planning_timing;
  if (plan?.source === "accepted_plan_estimate") {
    const start = clockMinutes(plan.start), end = clockMinutes(plan.end);
    if (start != null && end != null && end > start) {
      return { kind: "plan", date: day.date, start, end, startText: plan.start, endText: plan.end };
    }
  }
  // Compatibility for saved older trips only. Rich cards own their scoped
  // editorial advice; a legacy string cannot override it.
  if (!loc.companion_content) {
    const match = /^(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})$/.exec((loc.best_hours || "").trim());
    const start = match && clockMinutes(match[1]), end = match && clockMinutes(match[2]);
    if (start != null && end != null && end > start) {
      return { kind: "legacy_preference", date: day.date, start, end, startText: match[1], endText: match[2] };
    }
  }
  return null;
};

export const visitTimingStatus = (loc, day, now = japanMoment()) => {
  const timing = visitTiming(loc, day);
  if (!timing || timing.date !== now.date) return null;
  if (timing.kind === "plan") {
    return { ...timing, urgent: false, label: now.minutes > timing.end
      ? `Planned around ${timing.startText} — adjust at your pace`
      : `Planned around ${timing.startText} · flexible estimate` };
  }
  const label = now.minutes < timing.start ? `Suggested from ${timing.startText}`
    : now.minutes <= timing.end ? `Suggested window until ${timing.endText}`
    : "Past the suggested window today";
  return { ...timing, label, urgent: now.minutes >= timing.start && now.minutes <= timing.end && timing.end - now.minutes <= 60 };
};

export const upcomingVisit = (day, checklist = {}, skipped = new Set(), now = japanMoment()) => {
  if (day?.date !== now.date) return null;
  // A nudge must not quietly reorder the accepted route or send someone past
  // an unfinished visit. Optional meals are choices, not appointments.
  for (const block of day.time_blocks || []) {
    if (block.time_of_day === "travel") continue;
    for (const loc of block.locations || []) {
      const key = loc.visit_id || loc.name;
      if (loc.is_optional || ["completed", "skipped", "removed"].includes(checklist[key]) || skipped.has(key)) continue;
      const timing = visitTiming(loc, day);
      if (!timing) return null;
      const delta = timing.start - now.minutes;
      return delta >= 8 && delta <= 45 ? {
        loc, block, timing, delta,
        key: `${day.date}:${key}:${timing.kind}:${timing.startText}`,
      } : null;
    }
  }
  return null;
};
